import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  object,
  func,
  string,
  arrayOf,
  shape,
  number,
  bool,
  any,
} from "prop-types";

import {
  onboardUpdateState,
  getAddressList,
  updateUserInProfileSilent,
} from "../redux/actions";

import BasicInfoAddressView from "../components/BasicInfo/BasicInfoAddress/index";

import history from "../history";

import { useDebounce } from "../helpers/hooks";
import { getByPath } from "../helpers";
import {
  nonChangableTeamCodes,
  excludedUmbracoBranches,
} from "../helpers/helpers";
import getNearestBranch from "../helpers/nearestBranch";
import { branchPropTypes } from "../components/SelectBranch";
import { trackOnboardingStep } from "../helpers/productMetrics";

const route = "/onboard/cv-upload";

const transformAddressResultFields = (result, postcode) => ({
  addressLine1: result.line_1,
  addressLine2: result.line_2,
  addressLine3: result.line_3,
  town: result.town_or_city,
  county: result.county,
  country: "UK",
  postcode,
});

const useAddressState = (appData, matchedAddressList) => {
  const currentAddress = appData.address || {};
  const initialState = {
    branchCode: appData.responsibleTeam || "",
    postcode: currentAddress.postcode || "",
    addressLine1: currentAddress.addressLine1 || "",
    addressLine2: currentAddress.addressLine2 || "",
    addressLine3: currentAddress.addressLine3 || "",
    county: currentAddress.county || "",
    town: currentAddress.town || "",
    country: "UK",
    isManualAddress: false,
    selectedAddressIndex: -1,
    isFindingAddress: false,
  };

  const [state, setState] = useState(initialState);

  const handlers = {
    onChange: (key, val) =>
      setState((prevState) => ({ ...prevState, [key]: val })),
    onAddressSelect: (selectedAddressIndex) => {
      const selectedAddress = transformAddressResultFields(
        matchedAddressList[selectedAddressIndex],
        state.postcode
      );
      setState((prevState) => ({
        ...prevState,
        ...selectedAddress,
        selectedAddressIndex,
      }));
    },
    manualAddress: (val) =>
      setState((prevState) => ({ ...prevState, isManualAddress: val })),
    findAddress: (val) =>
      setState((prevState) => ({ ...prevState, isFindingAddress: val })),
    resetAddressIndex: () =>
      setState((prevState) => ({ ...prevState, selectedAddressIndex: -1 })),
  };

  return [state, handlers];
};

const BasicInfoAddress = (props) => {
  const {
    appData,
    updateProfile,
    branches: umbracoBranches,
    matchedAddressList,
    matchedPostcodePosition,
    description,
    postcodeError,
    updateOnboardState,
    onboardState,
    responsibleTeamCode,
    getAddressList: getAddressListFromProps,
  } = props;

  const [updateBranch, setUpdateBranch] = useState(false);
  const [branchCode, setBranchCode] = useState(responsibleTeamCode);
  const [state, handlers] = useAddressState(appData, matchedAddressList);
  const branches = umbracoBranches.filter(
    (branch) => !excludedUmbracoBranches.includes(branch.code)
  );

  const { candidateRef, emailAddress } = appData;
  const {
    addressLine1,
    addressLine2,
    addressLine3,
    county,
    town,
    country,
    postcode,
  } = state;

  const handleChange = (key, val) => {
    if (key === "postcode" && !nonChangableTeamCodes.includes(branchCode)) {
      setUpdateBranch(true);
    }
    handlers.onChange(key, val);
  };

  useEffect(() => {
    if (matchedPostcodePosition.lat && updateBranch) {
      setUpdateBranch(false);
      const branch = getNearestBranch(matchedPostcodePosition, branches);
      const responsibleTeam = branch ? branch.code : "";
      setBranchCode(responsibleTeam);
    }
  }, [matchedPostcodePosition.lat, matchedPostcodePosition.long]);

  const { findAddress, resetAddressIndex, manualAddress } = handlers;
  const actions = {
    handleSubmit: () => {
      const updatedUserData = {
        candidateRef,
        emailAddress,
        responsibleTeam: branchCode,
        address: {
          addressLine1,
          addressLine2,
          addressLine3,
          county,
          town,
          country,
          postcode,
          ...matchedPostcodePosition,
        },
      };
      return Promise.all([
        updateProfile(candidateRef, updatedUserData),
        updateOnboardState({
          ...onboardState,
          address: true,
        }),
        trackOnboardingStep("Location"),
        history.push(route),
      ]);
    },
    canFindAddress: state.postcode !== "",
    shouldShowAddresses: state.isFindingAddress,
    showManualAddress: () => {
      manualAddress(true);
      findAddress(false);
    },
    hideManualAddress: () => {
      manualAddress(false);
    },
    getMatchedAddress: () => {
      getAddressList(postcode);
      findAddress(true);
      manualAddress(false);
      resetAddressIndex();
    },
  };

  useDebounce(
    postcode,
    () => {
      getAddressListFromProps(postcode);
    },
    1500
  );

  const onBranchChange = (key, team) => setBranchCode(team);
  return (
    <BasicInfoAddressView
      {...props}
      {...actions}
      {...state}
      {...handlers}
      postcodeError={postcodeError}
      branches={branches}
      onChange={handleChange}
      responsibleTeamCode={branchCode}
      firstName={appData.firstName}
      description={description}
      onBranchChange={onBranchChange}
      route={route}
    />
  );
};

BasicInfoAddress.propTypes = {
  description: string.isRequired,
  appData: object.isRequired,
  updateProfile: func.isRequired,
  getAddressList: func.isRequired,
  branches: arrayOf(shape(branchPropTypes)),
  postcodeError: bool.isRequired,
  matchedPostcodePosition: shape({
    lat: number.isRequired,
    long: number.isRequired,
  }),
  matchedAddressList: arrayOf(
    shape({
      addressLine1: string.isRequired,
      addressLine2: string.isRequired,
      addressLine3: string.isRequired,
      town: string.isRequired,
      county: string.isRequired,
      postcode: string.isRequired,
    })
  ),
  updateOnboardState: func.isRequired,
  onboardState: any,
  responsibleTeamCode: string,
};

const mapStateToProps = (state) => ({
  appData: state.core.app,
  description: state.core.umbracoContent.addressSub,
  matchedAddressList: state.core.addressList.addresses,
  postcodeError: getByPath("core.postcodeError.response.status", state) === 400,
  matchedPostcodePosition: {
    lat: state.core.addressList.latitude,
    long: state.core.addressList.longitude,
  },
  branches: state.core.umbracoContent.branches,
  loading: state.core.loading,
  onboardState: state.core.onboardState,
  responsibleTeamCode: state.core.app.responsibleTeam,
});
const mapDispatchToProps = {
  updateOnboardState: onboardUpdateState,
  getAddressList,
  updateProfile: updateUserInProfileSilent,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(BasicInfoAddress);
